import React from "react";
import PropTypes from "prop-types";
import { Plus } from "lucide-react";

import { UIButton } from "../../ui";
import { clx } from "../../../utils/Helpers";
import "./DashboardEmptyState.scss";

/**
 * Shared empty state for dashboard section cards (company + admin portals) —
 * one look everywhere: a dashed box with a round icon badge, title, description
 * and an optional "Create Event" style action. `icon` is a lucide element; its
 * size and colour come from the badge (see the scss).
 */
const DashboardEmptyState = ({
  icon,
  title,
  description,
  actionLabel,
  actionIcon,
  onAction,
  tone,
  className,
}) => (
  <div
    className={clx(
      "dashboard-empty",
      tone !== "neutral" && `dashboard-empty--${tone}`,
      className,
    )}
  >
    <span className="dashboard-empty__icon" aria-hidden="true">
      {icon}
    </span>
    <span className="dashboard-empty__title">{title}</span>
    {description ? (
      <p className="dashboard-empty__description">{description}</p>
    ) : null}
    {actionLabel && onAction ? (
      <UIButton
        variant="outline"
        size="medium"
        type="button"
        className="dashboard-empty__action"
        startIcon={actionIcon}
        onClick={onAction}
      >
        {actionLabel}
      </UIButton>
    ) : null}
  </div>
);

DashboardEmptyState.propTypes = {
  icon: PropTypes.node.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  actionLabel: PropTypes.string,
  actionIcon: PropTypes.node,
  onAction: PropTypes.func,
  /** "success" tints the icon badge + title green — a positive empty state (e.g. no pending requests). */
  tone: PropTypes.oneOf(["neutral", "success"]),
  className: PropTypes.string,
};

DashboardEmptyState.defaultProps = {
  description: "",
  actionLabel: "",
  actionIcon: <Plus size={16} strokeWidth={2} />,
  onAction: undefined,
  tone: "neutral",
  className: "",
};

export default DashboardEmptyState;
